import type { ReactNode } from 'react';
import { IconSizes, type NewcoreIcon } from '@newcore/icons';
import { FontWeights } from '../../enums/font-weights';
import { Sizes } from '../../enums/sizes';
import { TextSizes } from '../../enums/typography-text-sizes';
import { BadgeSizes, BadgeVariants } from '../../enums/variants';
import { cn } from '../../utils/cn';
import { textVariants } from '../../variants/typography-text-variants';
import { Badge } from '../badges/badge';
import { Tooltip } from '../tooltips';

export type SegmentedControlSize = Sizes.SM | Sizes.MD;

const ICON_ONLY_SIZE_CLASSES: Record<SegmentedControlSize, string> = {
  [Sizes.SM]: 'size-7',
  [Sizes.MD]: 'size-8',
};

interface SegmentedControlButtonProps {
  ref: (el: HTMLButtonElement | null) => void;
  value: string;
  label: ReactNode;
  icon?: NewcoreIcon;
  count?: number;
  size: SegmentedControlSize;
  isActive: boolean;
  disabled?: boolean;
  onClick: () => void;
}

interface SegmentedControlLabelProps {
  label: ReactNode;
  count?: number;
}

function SegmentedControlLabel({ label, count }: SegmentedControlLabelProps) {
  return (
    <span className='inline-flex items-center gap-1'>
      {label}
      {count !== undefined && (
        <Badge variant={BadgeVariants.MODERN} size={BadgeSizes.SM}>
          {count}
        </Badge>
      )}
    </span>
  );
}

export function SegmentedControlButton({
  ref,
  value,
  label,
  icon: Icon,
  count,
  size,
  isActive,
  disabled,
  onClick,
}: SegmentedControlButtonProps) {
  const isIconOnly = Boolean(Icon);

  return (
    <Tooltip content={label} disabled={!isIconOnly}>
      <button
        ref={ref}
        type='button'
        role='radio'
        aria-checked={isActive}
        data-testid={`segmented-control-option-${value}`}
        disabled={disabled}
        onClick={onClick}
        className={cn(
          'relative z-10 inline-flex items-center justify-center rounded-md transition-colors',
          isIconOnly
            ? ICON_ONLY_SIZE_CLASSES[size]
            : cn(
                'h-8 px-2',
                textVariants({
                  size: TextSizes.SM,
                  weight: FontWeights.SEMIBOLD,
                })
              ),
          isActive && 'text-primary',
          !isActive &&
            (isIconOnly
              ? 'text-quaternary hover:text-tertiary'
              : 'text-tertiary hover:text-secondary'),
          disabled && 'cursor-not-allowed'
        )}
      >
        {Icon ? (
          <>
            <Icon size={IconSizes.SM} />
            <span className='sr-only'>{label}</span>
          </>
        ) : (
          <SegmentedControlLabel label={label} count={count} />
        )}
      </button>
    </Tooltip>
  );
}
